import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { fmtDate, toInputDate } from '../utils';
import { Modal, Pagination } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['Open','In Progress','Done','Cancelled'];
const PRIORITIES = ['Critical','High','Medium','Low'];
const PRIORITY_COLORS = { Critical:'#dc2626',High:'#f97316',Medium:'#f59e0b',Low:'#16a34a' };
const STATUS_COLORS = { Open:'#3b82f6','In Progress':'#f59e0b',Done:'#16a34a',Overdue:'#dc2626' };
const STATUS_BG = { Open:'#eff6ff','In Progress':'#fffbeb',Done:'#f0fdf4',Overdue:'#fee2e2' };

function NewTaskModal({ users, onSave, onClose }) {
  const [form, setForm] = useState({
    title:'', description:'', notes:'',
    assigned_to:'', due_date:'', status:'Open', priority:'Medium',
    related_type:'opportunity', related_id:''
  });
  const [relatedOptions, setRelatedOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  React.useEffect(() => {
    const endpoints = {
      opportunity: '/opportunities?limit=200',
      contract:    '/contracts?limit=200',
      account:     '/accounts?limit=200',
    };
    const ep = endpoints[form.related_type];
    if (ep) {
      api.get(ep).then(r => setRelatedOptions(r.data.data || [])).catch(() => setRelatedOptions([]));
    }
    setForm(f => ({ ...f, related_id:'' }));
  }, [form.related_type]);

  const getLabel = (item) => {
    if (form.related_type === 'opportunity') return item.name || item.opportunity_id;
    if (form.related_type === 'contract') return `${item.contract_number} — ${item.name}`;
    return item.name;
  };

  const handleSave = async () => {
    if (!form.title.trim()) return setError('Title is required');
    if (!form.related_id) return setError('Please select a linked record');
    setSaving(true); setError('');
    try {
      // opportunities route is /opportunities/:id/tasks
      // contracts route is /contracts/:id/tasks
      // accounts route is /accounts/:id/tasks
      const pluralMap = { opportunity: 'opportunities', contract: 'contracts', account: 'accounts' };
      const endpoint = `/${pluralMap[form.related_type]}/${form.related_id}/tasks`;
      await api.post(endpoint, {
        title: form.title,
        description: form.description,
        notes: form.notes,
        assigned_to: form.assigned_to || undefined,
        due_date: form.due_date || undefined,
        status: form.status,
        priority: form.priority,
      });
      onSave();
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Create New Task" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input className="form-input" value={form.title} onChange={set('title')} placeholder="Task title..." autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-textarea" value={form.description} onChange={set('description')} rows={2} placeholder="Optional details..." />
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select className="form-select" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input className="form-input" type="date" value={form.due_date} onChange={set('due_date')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Assign To</label>
          <select className="form-select" value={form.assigned_to} onChange={set('assigned_to')}>
            <option value="">Unassigned</option>
            {users.filter(u => !u.email?.startsWith('admin@')).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="section-divider">Link to Record</div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Record Type</label>
            <select className="form-select" value={form.related_type} onChange={set('related_type')}>
              <option value="opportunity">Opportunity</option>
              <option value="contract">Contract</option>
              <option value="account">Account</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Select Record *</label>
            <select className="form-select" value={form.related_id} onChange={set('related_id')}>
              <option value="">Choose...</option>
              {relatedOptions.map(item => (
                <option key={item.id} value={item.id}>{getLabel(item)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : 'Create Task'}</button>
      </div>
    </Modal>
  );
}

function TaskEditModal({ task, users, onSave, onClose }) {
  const [form, setForm] = useState({
    title:       task.title       || '',
    description: task.description || '',
    notes:       task.notes       || '',
    assigned_to: task.assigned_to || '',
    // toInputDate strips the T00:00:00.000Z so the date input shows correctly
    due_date:    toInputDate(task.due_date),
    status:      task.status      || 'Open',
    priority:    task.priority    || 'Medium',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const effectiveStatus = () => {
    if (form.status === 'Done') return 'Done';
    if (form.due_date && new Date(form.due_date) < new Date()) return 'Overdue';
    return form.status;
  };

  const handleSave = async () => {
    if (!form.title.trim()) return setError('Title required');
    setSaving(true); setError('');
    try {
      const r = await api.put(`/tasks/${task.id}`, { ...form, status: effectiveStatus() });
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Edit Task" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input className="form-input" value={form.title} onChange={set('title')} autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-textarea" value={form.description} onChange={set('description')} rows={2} />
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={set('status')}>
              {['Open','In Progress','Done','Cancelled'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select className="form-select" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Assigned To</label>
            <select className="form-select" value={form.assigned_to} onChange={set('assigned_to')}>
              <option value="">Unassigned</option>
              {users.filter(u => !u.email?.startsWith('admin@')).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input className="form-input" type="date" value={form.due_date} onChange={set('due_date')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Notes / Update</label>
          <textarea className="form-textarea" value={form.notes} onChange={set('notes')} rows={3} placeholder="Add progress notes, blockers, or updates..." />
        </div>
        <div style={{ fontSize:12,color:'var(--gray-500)',background:'var(--gray-50)',padding:'8px 12px',borderRadius:6 }}>
          Linked to: <strong style={{ textTransform:'capitalize' }}>{task.related_type}</strong>
          {task.created_by_name && <span> · Created by {task.created_by_name}</span>}
          {task.created_at && <span> · {fmtDate(task.created_at)}</span>}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
      </div>
    </Modal>
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(1);
  const [status, setStatus] = useState(''); const [priority, setPriority] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, priority, page, limit: 50 };
      if (assignedToMe && user?.id) params.assigned_to = user.id;
      const r = await api.get('/tasks', { params });
      let data = r.data.data;
      if (year) data = data.filter(t => {
        const d = t.due_date || t.created_at;
        return d && String(d).startsWith(year);
      });
      setTasks(data); setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [status, priority, year, assignedToMe, page, user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {}); }, []);

  const handleSave = (updated) => {
    setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
    setEditTask(null);
  };

  const cycleStatus = async (task) => {
    const next = task.status === 'Done' ? 'Open' : task.status === 'Open' ? 'In Progress' : 'Done';
    const r = await api.put(`/tasks/${task.id}`, { status: next });
    setTasks(prev => prev.map(t => t.id === task.id ? r.data : t));
  };

  const isOverdue = (t) => t.due_date && t.status !== 'Done' && new Date(toInputDate(t.due_date)) < new Date(new Date().toISOString().split('T')[0]);
  const daysUntil = (d) => {
    if (!d) return null;
    return Math.ceil((new Date(toInputDate(d)) - new Date(new Date().toISOString().split('T')[0]))/(1000*60*60*24));
  };

  const getYear = (t) => {
    const d = t.due_date || t.created_at;
    return d ? String(d).substring(0,4) : '—';
  };

  const YEARS = [2023,2024,2025,2026,2027,2028];

  return (
    <>
      <div className="page-header">
        <span className="page-title">All Tasks</span>
        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
          <button className="btn btn-primary" onClick={() => setShowNewTask(true)}>+ New Task</button>
        </div>
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body" style={{ paddingBottom:12 }}>
            <div className="toolbar">
              <select className="filter-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                <option value="">All Statuses</option>
                {[...STATUSES,'Overdue'].map(s => <option key={s}>{s}</option>)}
              </select>
              <select className="filter-select" value={priority} onChange={e => { setPriority(e.target.value); setPage(1); }}>
                <option value="">All Priorities</option>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
              <select className="filter-select" value={year} onChange={e => { setYear(e.target.value); setPage(1); }}>
                <option value="">All Years</option>
                {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
              </select>
              <label style={{ display:'flex',alignItems:'center',gap:7,fontSize:13,color:'var(--gray-600)',cursor:'pointer',userSelect:'none' }}>
                <input type="checkbox" checked={assignedToMe} onChange={e => { setAssignedToMe(e.target.checked); setPage(1); }} />
                My tasks only
              </label>
              <span className="text-muted text-sm" style={{ marginLeft:'auto' }}>{tasks.length} shown</span>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width:36 }}></th>
                  <th>Task</th>
                  <th>Assigned To</th>
                  <th>Linked To</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Year</th>
                  <th style={{ width:60 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} style={{ textAlign:'center',padding:40,color:'var(--gray-400)' }}>Loading...</td></tr>}
                {!loading && tasks.length === 0 && (
                  <tr><td colSpan={9}>
                    <div className="empty-state"><div className="empty-state-icon">✅</div><div className="empty-state-text">No tasks found</div></div>
                  </td></tr>
                )}
                {tasks.map(t => {
                  const overdue = isOverdue(t) || t.status === 'Overdue';
                  const days = daysUntil(t.due_date);
                  return (
                    <tr key={t.id} style={{ opacity: t.status === 'Done' ? 0.5 : 1 }}>
                      <td onClick={e => e.stopPropagation()}>
                        <div onClick={() => cycleStatus(t)} title="Click to cycle status"
                          style={{ width:22,height:22,borderRadius:5,border:`2px solid ${STATUS_COLORS[t.status]||'var(--gray-300)'}`,background:t.status==='Done'?STATUS_COLORS['Done']:t.status==='Overdue'?'#fee2e2':'transparent',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s' }}>
                          {t.status==='Done' && <span style={{ color:'white',fontSize:12,fontWeight:700 }}>✓</span>}
                          {t.status==='In Progress' && <span style={{ color:STATUS_COLORS['In Progress'],fontSize:10,fontWeight:700 }}>…</span>}
                          {t.status==='Overdue' && <span style={{ color:'#dc2626',fontSize:11,fontWeight:700 }}>!</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight:500,fontSize:13,textDecoration:t.status==='Done'?'line-through':'none',color:t.status==='Done'?'var(--gray-400)':'var(--gray-800)' }}>{t.title}</div>
                        {t.description && <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:1 }}>{t.description}</div>}
                        {t.notes && <div style={{ fontSize:12,color:'var(--primary)',marginTop:2,fontStyle:'italic' }}>📝 {t.notes.substring(0,60)}{t.notes.length>60?'...':''}</div>}
                      </td>
                      <td style={{ fontSize:13 }}>{t.assigned_to_name || <span className="text-muted">—</span>}</td>
                      <td>
                        <span className="td-link" style={{ fontSize:12,cursor:'pointer',textTransform:'capitalize' }}
                          onClick={() => { const pluralMap = { opportunity:'opportunities', contract:'contracts', account:'accounts', contact:'contacts' }; t.related_id && navigate(`/${pluralMap[t.related_type] || t.related_type + 's'}/${t.related_id}`); }}>
                          {t.related_type} →
                        </span>
                      </td>
                      <td><span style={{ fontSize:12,fontWeight:600,color:PRIORITY_COLORS[t.priority]||'var(--gray-500)' }}>{t.priority}</span></td>
                      <td>
                        <span style={{ fontSize:12,fontWeight:600,color:STATUS_COLORS[t.status]||'var(--gray-600)',background:STATUS_BG[t.status]||'var(--gray-100)',padding:'3px 9px',borderRadius:12,whiteSpace:'nowrap' }}>
                          {t.status}
                        </span>
                      </td>
                      <td style={{ fontSize:13 }}>
                        {t.due_date
                          ? <span style={{ color:overdue?'var(--danger)':days===0?'var(--warning)':'var(--gray-700)',fontWeight:overdue||days===0?600:400 }}>
                              {fmtDate(t.due_date)}
                              {overdue && t.status!=='Done' && <span style={{ marginLeft:4,fontSize:11 }}>⚠️</span>}
                              {!overdue && days===0 && <span style={{ marginLeft:4,fontSize:11 }}>Today</span>}
                            </span>
                          : <span className="text-muted">—</span>
                        }
                      </td>
                      <td style={{ fontSize:12,color:'var(--gray-500)' }}>{getYear(t)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Edit task" onClick={() => setEditTask(t)}>✏️</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} limit={50} total={total} onPage={setPage} />
        </div>
      </div>
      {editTask && <TaskEditModal task={editTask} users={users} onSave={handleSave} onClose={() => setEditTask(null)} />}
      {showNewTask && <NewTaskModal users={users} onSave={() => { setShowNewTask(false); load(); }} onClose={() => setShowNewTask(false)} />}
    </>
  );
}
